/**
 * One monitoring run — the borrower-period ledger the run page is about.
 *
 * Refetch is hold-and-dim, the same contract `useKpiTable` states: the run on
 * screen stays mounted while the next answer loads and is marked `stale` so
 * the page can dim it and stop it being read as settled. Blanking to a
 * skeleton after every manual entry would read as a page reload for what is
 * really one cell moving.
 *
 * While the server says `generating`, the run is polled every 5 s: generation
 * is a background job with no push channel, and the ledger it produces is the
 * whole page.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { monitoringService } from "../services/monitoringService";
import type { MonitoringRun } from "../types/monitoring";
import { isRateLimited } from "../utils/isRateLimited";

/** How often a generating run is re-read. */
export const MONITORING_RUN_POLL_MS = 5000;

export interface MonitoringRunData {
	run: MonitoringRun | null;
	/** Nothing to show yet — the first load for this run id. */
	loading: boolean;
	error: Error | undefined;
	/**
	 * A refetch is in flight and the run on screen is the PREVIOUS answer.
	 * The page dims it; it never hides it.
	 */
	stale: boolean;
	/** Re-read the run. Every write on the page awaits this. */
	refresh: () => Promise<void>;
}

export function useMonitoringRun(runId: string | undefined): MonitoringRunData {
	const [run, setRun] = useState<MonitoringRun | null>(null);
	const [loading, setLoading] = useState(true);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	// A 429 stops the poll. Polling into a spent budget only keeps it spent;
	// the next read that succeeds (a manual refresh, a write's re-read) resumes it.
	const [rateLimited, setRateLimited] = useState(false);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Rising counter, so a slow response for the previous run id cannot
	// overwrite the current one when the user walks the board.
	const requestId = useRef(0);

	const load = useCallback(
		async (initial: boolean) => {
			if (!runId) return;
			const id = ++requestId.current;
			if (initial) setLoading(true);
			else setStale(true);
			try {
				const next = await monitoringService.getRun(runId);
				if (!mounted.current || id !== requestId.current) return;
				setRun(next);
				setError(undefined);
				setRateLimited(false);
			} catch (caught) {
				if (!mounted.current || id !== requestId.current) return;
				const failure =
					caught instanceof Error ? caught : new Error(String(caught));
				setError(failure);
				if (isRateLimited(failure)) setRateLimited(true);
				// An initial load that failed has nothing to hold: showing an
				// empty page under an error banner beats a blank one.
				if (initial) setRun(null);
			} finally {
				if (mounted.current && id === requestId.current) {
					setLoading(false);
					setStale(false);
				}
			}
		},
		[runId],
	);

	useEffect(() => {
		// A different run is a different answer: drop the one on screen rather
		// than dimming another borrower's numbers under this borrower's heading.
		setRun(null);
		setError(undefined);
		setLoading(true);
		void load(true);
	}, [load]);

	const refresh = useCallback(() => load(false), [load]);

	const generating = run?.generating ?? false;
	useEffect(() => {
		if (!generating || !runId || rateLimited) return;
		const timer = window.setInterval(() => {
			void load(false);
		}, MONITORING_RUN_POLL_MS);
		return () => window.clearInterval(timer);
	}, [generating, runId, rateLimited, load]);

	return { run, loading, error, stale, refresh };
}
